/**
 * Просьба оставить отзыв на Яндекс.Картах — после того, как человек поставил
 * в приложении высшую оценку.
 *
 * Зачем: поток отзывов на карточках встал (последний был 17 июля при 199
 * отзывах всего), а Яндекс учитывает свежесть — карточка без новых отзывов
 * опускается в выдаче. Просим только тех, кто и так доволен, и только в момент,
 * когда он сам об этом сказал.
 *
 * Правила частоты задал владелец: не чаще раза в три месяца, а если человек
 * нажал «уже оставила» — не просим никогда.
 */
const ASKED_AT = 'yandexReviewAskedAt';
const DONE = 'yandexReviewDone';

/** Три месяца между просьбами: реже уже бессмысленно, чаще — назойливо. */
const COOLDOWN_DAYS = 90;

/** Ссылки на отзывы конкретного филиала: человек должен попасть сразу в свой. */
const YANDEX_BY_BRANCH: Record<string, string> = {
  recEX0maoE8xTOr8J: 'https://yandex.by/maps/org/177477136833/reviews/', // Грушевка
  recM9FjRGHh8cNEdX: 'https://yandex.by/maps/org/194870670219/reviews/', // Немига
  recQBzoW5g7YOcAg0: 'https://yandex.by/maps/org/59823006490/reviews/', // Восток
  rechPCxFyutEOlN9R: 'https://yandex.by/maps/org/53457505119/reviews/', // Пушкинская
  recNX8beFpCcGKP3l: 'https://yandex.by/maps/org/53457505119/reviews/', // Пушкинская, зал 2
};

/** Карточки в Google — те же филиалы, идентификаторы из нашего бизнес-профиля. */
const GOOGLE_BY_BRANCH: Record<string, string> = {
  recEX0maoE8xTOr8J: 'https://www.google.com/maps?cid=12601070914641137249', // Грушевка
  recM9FjRGHh8cNEdX: 'https://www.google.com/maps?cid=14069382073032024177', // Немига
  recQBzoW5g7YOcAg0: 'https://www.google.com/maps?cid=11004965349523088970', // Восток
  rechPCxFyutEOlN9R: 'https://www.google.com/maps?cid=17671060281853140414', // Пушкинская
  recNX8beFpCcGKP3l: 'https://www.google.com/maps?cid=17671060281853140414', // Пушкинская, зал 2
};

/** Если филиал неизвестен, ведём на Грушевку — там больше всего отзывов. */
const YANDEX_FALLBACK = 'https://yandex.by/maps/org/177477136833/reviews/';
const GOOGLE_FALLBACK = 'https://www.google.com/maps?cid=12601070914641137249';

export const yandexUrlForBranch = (branchRecId?: string | null): string =>
  (branchRecId && YANDEX_BY_BRANCH[branchRecId]) || YANDEX_FALLBACK;

export const googleUrlForBranch = (branchRecId?: string | null): string =>
  (branchRecId && GOOGLE_BY_BRANCH[branchRecId]) || GOOGLE_FALLBACK;

/**
 * События воронки: показали просьбу, перешли на площадку, отказались.
 * Best-effort — молча глотаем ошибку, аналитика не должна мешать человеку.
 */
export type AskStage = 'shown' | 'clicked' | 'already';

export const trackAsk = (
  stage: AskStage,
  payload: { platform?: 'yandex' | 'google'; branchRecId?: string | null; hadText?: boolean } = {}
) => {
  try {
    const body = JSON.stringify({ stage, ...payload });
    // REACT_APP_API_URL смотрит на GraphQL, а события принимает REST-хост —
    // поэтому адрес отдельный.
    const url = `${process.env.REACT_APP_REST_URL || 'https://api.amani-dance.com'}/api/review-ask`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
  } catch {
    /* аналитика не имеет права ломать сценарий */
  }
};

export const canAskForYandexReview = (): boolean => {
  try {
    if (localStorage.getItem(DONE) === '1') return false;
    const asked = localStorage.getItem(ASKED_AT);
    if (!asked) return true;
    const days = (Date.now() - new Date(asked).getTime()) / (1000 * 60 * 60 * 24);
    return Number.isFinite(days) && days >= COOLDOWN_DAYS;
  } catch {
    // приватный режим или заблокированное хранилище — лучше не просить вовсе,
    // чем просить каждый раз
    return false;
  }
};

export const markYandexReviewAsked = () => {
  try {
    localStorage.setItem(ASKED_AT, new Date().toISOString());
  } catch {
    /* пусто */
  }
};

export const markYandexReviewDone = () => {
  try {
    localStorage.setItem(DONE, '1');
  } catch {
    /* пусто */
  }
};
